import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Alert,
    Button,
    CardActions,
    CardContent,
    CardHeader,
    FormControlLabel,
    Grid,
    Switch,
    TextField,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { setModal } from '../../../../../Common/App';
import { DialogCard, Loader } from '../../../../../../Components';
import { addEvent, updateEvent } from '../../../Redux/Action';

export const EventModal = ({ item }) => {

    const dispatch = useDispatch();
    const [disabled] = useState(false);
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState({
        id: null,
        event_name: '',
        description: '',
        venue_id: '',
        location_id: '',
        start_date: null,
        end_date: null,
        is_active: true,
    });

    useEffect(() => {
        if (item) {
            setData({
                id: item.id,
                event_name: item.event_name || '',
                description: item.description || '',
                venue_id: item.venue_id || '',
                location_id: item.location_id || '',
                start_date: item.start_date ? dayjs(item.start_date) : null,
                end_date: item.end_date ? dayjs(item.end_date) : null,
                is_active: item.is_active !== false && item.is_active !== 0,
            });
        }
    }, [item]);

    const handleSaveOrUpdate = async () => {

        if (!data.event_name.trim()) {
            setErrors({ event_name: 'Event name is required' });
            setMessage('Event name is required');
            return;
        }

        if (data.start_date && data.end_date && data.end_date.isBefore(data.start_date, 'day')) {
            setErrors({ end_date: 'End date must be on or after start date' });
            setMessage('End date must be on or after start date');
            return;
        }

        setIsLoading(true);
        const payload = {
            event_name: data.event_name.trim(),
            description: data.description?.trim() || null,
            venue_id: data.venue_id?.trim() || null,
            location_id: data.location_id?.trim() || null,
            start_date: data.start_date ? data.start_date.format('YYYY-MM-DD') : null,
            end_date: data.end_date ? data.end_date.format('YYYY-MM-DD') : null,
            is_active: !!data.is_active,
        };

        if (data.id) {
            payload.id = data.id;
            await updateEvent({ dispatch, data: payload }).then((res) => {
                setIsLoading(false);
                if (res.success) {
                    dispatch(setModal({ component: null, open: false }));
                } else {
                    setErrors(res.errors || {});
                    setMessage(res.message || '');
                }
            });
        } else {
            await addEvent({ dispatch, data: payload }).then((res) => {
                setIsLoading(false);
                if (res.success) {
                    dispatch(setModal({ component: null, open: false }));
                } else {
                    setErrors(res.errors || {});
                    setMessage(res.message || '');
                }
            });
        }
    };

    const handleChange = (e) => {
        setErrors({});
        setMessage('');
        const { name, value, checked, type } = e.target;
        setData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleActiveChange = (e) => {
        setErrors({});
        setMessage('');
        setData((prev) => ({
            ...prev,
            is_active: e.target.checked,
        }));
    };

    return (
        <DialogCard>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <CardHeader
                    title={data.id ? 'Edit Event' : 'Add Event'}
                    avatar={<Loader isLoading={isLoading} type="linear" />}
                />
                <CardContent>
                    {message && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {message}
                        </Alert>
                    )}
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Event name"
                                name="event_name"
                                value={data.event_name}
                                onChange={handleChange}
                                error={!!errors.event_name}
                                helperText={errors.event_name}
                                disabled={disabled}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                hidden={true}
                                fullWidth
                                label="Description"
                                name="description"
                                value={data.description}
                                onChange={handleChange}
                                error={!!errors.description}
                                helperText={errors.description}
                                disabled={disabled}
                                multiline
                                minRows={2}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                hidden={true}
                                fullWidth
                                label="Venue ID (optional)"
                                name="venue_id"
                                value={data.venue_id}
                                onChange={handleChange}
                                error={!!errors.venue_id}
                                helperText={errors.venue_id || 'Paste venue ULID if linking'}
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                hidden={true}
                                fullWidth
                                label="Location ID (optional)"
                                name="location_id"
                                value={data.location_id}
                                onChange={handleChange}
                                error={!!errors.location_id}
                                helperText={errors.location_id || 'Must belong to venue when both set'}
                                disabled={disabled}
                            />
                        </Grid>
                        {/* <Grid item xs={12} sm={6}>
                            <DatePicker
                                hidden={true}
                                label="Start date (optional)"
                                value={data.start_date}
                                onChange={(v) => {
                                    setErrors({});
                                    setMessage('');
                                    setData((prev) => ({ ...prev, start_date: v }));
                                }}
                                slotProps={{
                                    textField: {
                                        hidden: true,
                                        fullWidth: true,
                                        error: !!errors.start_date,
                                        helperText: errors.start_date,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DatePicker
                                hidden={true}
                                label="End date (optional)"
                                value={data.end_date}
                                onChange={(v) => {
                                    setErrors({});
                                    setMessage('');
                                    setData((prev) => ({ ...prev, end_date: v }));
                                }}
                                slotProps={{
                                    textField: {
                                        hidden: true,
                                        fullWidth: true,
                                        error: !!errors.end_date,
                                        helperText: errors.end_date,
                                    },
                                }}
                            />
                        </Grid> */}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={!!data.is_active}
                                        onChange={handleActiveChange}
                                        disabled={disabled}
                                        color="primary"
                                    />
                                )}
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={() => dispatch(setModal({ component: null, open: false }))}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveOrUpdate}
                        disabled={isLoading || disabled}
                    >
                        {isLoading ? 'Saving...' : (data.id ? 'Update' : 'Add')}
                    </Button>
                </CardActions>
            </LocalizationProvider>
        </DialogCard>
    );
};
